import { useEffect, useId, useRef, useState } from 'react';
import type { SavedArtifactDisplay } from '../../../../src/artifacts/saved-view-contract.mjs';
import { ScalarView, type ViewerProps } from '../document/blocks';
import { useTableCopy } from './copy';
import '../spreadsheet/sheet.css';

/** Literal canonical rows retain their declared column order and exact Scalar values. */
export function TableView({
  display,
  locale,
  className,
  style,
  embedded = false,
}: ViewerProps & {
  display: Extract<SavedArtifactDisplay, { kind: 'table' }>;
  embedded?: boolean;
}) {
  const tableCopy = useTableCopy();
  const scroll = useRef<HTMLDivElement>(null);
  const hintId = useId();
  const [overflow, setOverflow] = useState(false);
  useEffect(() => {
    const node = scroll.current;
    if (!embedded || !node) return;
    const measure = () => setOverflow(node.scrollWidth > node.clientWidth + 1);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(node);
    if (node.firstElementChild) observer?.observe(node.firstElementChild);
    return () => observer?.disconnect();
  }, [display, embedded]);
  const showHint = embedded && overflow;
  return (
    <section
      data-kind="table"
      data-inspect-node={display.id}
      aria-label={display.title}
      className={className}
      style={style}
    >
      {embedded ? (
        <p className="ws-doc-table-caption">{display.title}</p>
      ) : (
        <h1 className="ws-doc-title">{display.title}</h1>
      )}
      <div className="ws-sheet">
        <div
          className="ws-sheet-scroll"
          ref={scroll}
          tabIndex={showHint ? 0 : undefined}
          role={showHint ? 'region' : undefined}
          aria-label={showHint ? display.title : undefined}
          aria-describedby={showHint ? hintId : undefined}
        >
          <table className="ws-sheet-grid" aria-label={display.title}>
            <thead>
              <tr>
                {display.columns.map((column) => (
                  <th key={column.id} scope="col" data-inspect-node={column.id}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {display.rows.map((row) => (
                <tr key={row.id} data-inspect-node={row.id}>
                  {display.columns.map((column) => (
                    <td
                      key={column.id}
                      data-column-id={column.id}
                      data-align={row.cells[column.id].value.kind === 'decimal' ? 'end' : undefined}
                    >
                      <ScalarView scalar={row.cells[column.id].value} locale={locale} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showHint && (
        <p id={hintId} className="ws-table-scroll-hint">
          {tableCopy.scrollColumns}
        </p>
      )}
    </section>
  );
}
